// src/views/FeaturesPage.js
import React, { useMemo } from 'react';
import { 
    CheckCircle2, Users, FileText, Calendar, 
    Shield, Mail, ArrowRight, ChevronDown,
    Layers, Lock, Unlock, Clock, BarChart3, ArrowUpRight,
    MapPin, Route, TrendingUp, Smartphone
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// --- IMPORT YOUR SCREENSHOTS ---
import crmPreview from '../assets/crm-preview.png';
import financePreview from '../assets/finance-preview.png';
import hrPreview from '../assets/hr-preview.png';
import schedulePreview from '../assets/schedule-preview.png';
import marketingPreview from '../assets/marketing-preview.png';
import appStoreBadge from '../assets/app-store-badge.svg';
import googlePlayBadge from '../assets/google-play-badge.svg';
import mobileAppPreview from '../assets/mobile-app-preview.png';

const CANADIAN_TIMEZONES = new Set([
    'America/Toronto','America/Vancouver','America/Edmonton','America/Winnipeg',
    'America/Halifax','America/St_Johns','America/Regina','America/Whitehorse',
    'America/Yellowknife','America/Iqaluit','America/Moncton','America/Glace_Bay',
]);


const FeaturesPage = () => {
  const APP_SIGNUP_URL = "https://app.spatchy.co/pricing";
  const isCanadian = useMemo(() => 
    CANADIAN_TIMEZONES.has(Intl.DateTimeFormat().resolvedOptions().timeZone), []);
  const startingPrice = isCanadian ? '$59 CAD' : '$49 USD';

  // App store links (features page placement — campaign tagged)
  const IOS_APP_URL = "https://apps.apple.com/ca/app/spatchy-field-service/id6766078373?ct=website_features";
  const ANDROID_APP_URL = "https://play.google.com/store/apps/details?id=co.spatchy.app&hl=en&referrer=utm_source%3Dspatchy.co%26utm_medium%3Dfeatures";

  // --- DATA: THE 5 PILLARS ---
  const features = [
    {
      id: "routing",
      title: "Smart Routing & Territory Planning",
      headline: "More jobs. Same hours. Less driving.",
      description: "Schedule your days so jobs are close together. Stop sending your crew across town wasting fuel and time — and start fitting one more job into every single day. Spatchy optimizes your routes by geography, respects customer preferences, and loads Google Maps with every waypoint ready to go.",
      icon: <Route size={24} />,
      color: "text-cyan-400",
      bg: "bg-cyan-500/20",
      image: schedulePreview,
      primaryLink: "/features/smart-routing",
      badge: "Most Powerful Feature",
      points: [
        "One-click route optimization — daily or weekly",
        "Navigate directly from the app via Google Maps",
        "Drag & drop jobs between crews, then re-optimize",
        "Route Health score — catch inefficient days before they start",
        "Respects customer preferences: preferred days, time windows, and assigned crew"
      ]
    },
    {
      id: "crm",
      title: "CRM & Lead Management",
      headline: "Never lose a lead. Never forget a customer.",
      description: "A searchable database of every customer, complete with full interaction history. From the first phone call to the final payment, track every detail in one place — from your truck or your desk.",
      icon: <Users size={24} />,
      color: "text-green-400",
      bg: "bg-green-500/20",
      image: crmPreview,
      primaryLink: "/features/client-database",
      points: [
        "Client Database & Interaction History",
        "Lead Tracking Pipeline",
        // wave1-noog 5a4b3c2d: removed Google mention — no timeline, no claim
        "Microsoft 365 Sync"
      ]
    },
    {
      id: "finance",
      title: "Finance & Invoicing",
      headline: "Invoice before you leave the driveway.",
      description: "Turn a completed job into a professional invoice in one click. Build quotes, collect digital approvals, and let Stripe handle payments. Track expenses in the field and let the system chase overdue invoices for you.",
      icon: <FileText size={24} />,
      color: "text-emerald-400",
      bg: "bg-emerald-500/20",
      image: financePreview,
      primaryLink: "/features/invoicing-payments",
      points: [
        "Quote & Estimate Builder with digital approvals",
        "One-click job conversion — zero re-typing",
        "Invoicing & Stripe Payments",
        "Automated overdue reminders",
        "Expense tracking tied to specific jobs"
      ]
    },
    {
      id: "hr",
      title: "Team & HR",
      headline: "Run your crew like a real company.",
      description: "Digital employee files, time-off tracking, timesheets, and payroll prep — everything you need to manage a growing team without the paperwork. No per-seat charges. Add your whole crew.",
      icon: <Shield size={24} />,
      color: "text-purple-400",
      bg: "bg-purple-500/20",
      image: hrPreview,
      primaryLink: "/features/team-management",
      points: [
        "Employee timesheets — clock in from any device",
        "Full digital employee files and document storage",
        "Time-off tracking with calendar integration",
        "Payroll dashboard — approve, export, done"
      ]
    },
    {
      id: "schedule",
      title: "Calendar & Dispatching",
      headline: "See your whole crew. Manage every job.",
      description: "A drag-and-drop calendar built for trades businesses with teams. Schedule, reassign, and dispatch without a single phone call. Watch job statuses update in real time as your crew moves through the day.",
      icon: <Calendar size={24} />,
      color: "text-blue-400",
      bg: "bg-blue-500/20",
      image: schedulePreview,
      primaryLink: "/features/calendar-dispatching",
      points: [
        "Drag-and-drop scheduling",
        // wave1-fix 1f9e0a4c: guard is a client-side warning for vehicles/equipment only — soften absolute "no double-booking"
        "Resource conflict detection — flags vehicle & equipment conflicts",
        "Real-time job status tracking",
        // wave1-noog 3a2b1c0d: removed Google mention, kept one-way phrasing — no timeline, no claim
        "Jobs sync to your Microsoft calendar"
      ]
    },
    {
      id: "email-campaigns",
      title: "Marketing & Growth",
      headline: "Turn quiet months into booked-out months.",
      description: "Send targeted emails to the right customers at the right time. Re-engage past clients, fill slow weeks, and drive repeat revenue — directly from inside Spatchy. No separate Mailchimp account needed.",
      icon: <Mail size={24} />,
      color: "text-orange-400",
      bg: "bg-orange-500/20",
      image: marketingPreview,
      primaryLink: "/features/email-campaigns",
      points: [
        "Custom segmented email campaigns",
        "The Client Portal — quotes, invoices, payments self-serve",
        "Campaign open and click analytics"
      ]
    }
  ];

  // --- DATA: THE FULL DIRECTORY ---
  const featureDirectory = [
    {
        category: "Smart Routing & Territory Planning",
        items: [
            { name: "Route Optimization — Daily & Weekly", slug: "smart-routing" },
            { name: "Google Maps Navigation Integration", slug: "smart-routing" },
            { name: "Multi-Crew Drag & Drop Grid", slug: "smart-routing" },
            { name: "Route Health Score", slug: "smart-routing" },
        ]
    },
    {
        category: "CRM & Lead Management",
        items: [
            { name: "Client Database & History", slug: "client-database" },
            { name: "Lead Tracking Pipeline", slug: "lead-tracking" },
            // wave1-noog 4a3b2c1d: removed Google mention — no timeline, no claim
            { name: "Microsoft 365 Sync", slug: "google-microsoft-sync" }
        ]
    },
    {
        category: "Finance & Invoicing",
        items: [
            { name: "Quote & Estimate Builder", slug: "quote-builder" },
            { name: "Invoicing & Stripe Payments", slug: "invoicing-payments" },
            { name: "Automated Overdue Reminders", slug: "overdue-reminders" },
            { name: "Expense Tracking", slug: "expense-tracking" },
            { name: "QuickBooks Online Sync", slug: "quickbooks-sync" }
        ]
    },
    {
        category: "Smart Scheduling & Ops",
        items: [
            { name: "Calendar & Dispatching", slug: "calendar-dispatching" },
            { name: "Job Tracking", slug: "job-tracking" }
        ]
    },
    {
        category: "Team & HR",
        items: [
            { name: "Employee Timesheets", slug: "team-management" },
            { name: "Digital Employee Files", slug: "employee-files" },
            { name: "Time-Off Tracking", slug: "time-off-tracking" },
            { name: "Payroll Ready", slug: "payroll-ready" }
        ]
    },
    {
        category: "Marketing & Growth",
        items: [
            { name: "Custom Email Campaigns", slug: "email-campaigns" },
            { name: "The Client Portal", slug: "client-portal" }
        ]
    },
    {
        category: "Reporting & Analytics",
        items: [
            { name: "Financial & Operational Reports", slug: "reporting-analytics" }
        ]
    }
  ];

  return (
    <div className="bg-slate-900 min-h-screen text-white pt-24 pb-32 font-sans selection:bg-blue-500 selection:text-white">
      <Helmet>
        <title>Features | Spatchy — Field Service Software Without the Gatekeeping</title>
        <meta name="description" content="Route optimization, CRM, invoicing, HR, and marketing tools — every feature included on every plan. Built for service businesses with teams." />
      </Helmet>

      {/* --- HERO HEADER --- */}
      <div className="text-center max-w-4xl mx-auto px-6 mb-24 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
          Everything your crew needs. <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
            On every plan. No exceptions.
          </span>
        </h1>
        <p className="text-xl text-slate-400 max-w-2xl mx-auto">
          From routing your crews to chasing overdue invoices — Spatchy handles the operations so you can focus on the work.
        </p>
        <div className="flex justify-center mt-12 opacity-50 animate-bounce">
            <ChevronDown className="text-white" size={32} />
        </div>
      </div>

      {/* --- ROUTING SPOTLIGHT BANNER --- */}
      <section className="px-6 mb-24">
        <div className="max-w-6xl mx-auto bg-gradient-to-r from-cyan-900/40 to-slate-900 border border-cyan-500/30 rounded-3xl p-8 md:p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-[80px] -z-10" />
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold uppercase mb-6">
                <TrendingUp size={12} /> Most Powerful Feature
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
                Stop driving all over town.<br />
                <span className="text-cyan-400">Fit one more job into every day.</span>
              </h2>
              <p className="text-slate-400 text-lg mb-6 leading-relaxed">
                Spatchy's route optimization groups your jobs by geography, respects customer preferences, and loads Google Maps with every stop pre-ordered. One extra job per crew per day is thousands in added revenue per year — for zero extra labour.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Optimize a full week or a single day in one click",
                  "Navigate via Google Maps with all waypoints ready",
                  "Drag jobs between crews and re-optimize instantly",
                  "Route Health score flags inefficient days before they start"
                ].map((point, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300">
                    <MapPin size={16} className="text-cyan-400 mt-0.5 shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
              <Link
                to="/features/smart-routing"
                className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-lg transition-all"
              >
                See Route Optimization in Depth <ArrowRight size={18} />
              </Link>
            </div>
            <div className="bg-slate-800 rounded-2xl border border-white/10 p-2 shadow-2xl">
              <img src={schedulePreview} alt="Spatchy Route Optimization" className="w-full h-auto rounded-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* --- SECTION 1: THE STACK BREAKER --- */}
      <section className="px-6 mb-40">
        <div className="max-w-6xl mx-auto bg-slate-800/30 border border-white/5 rounded-3xl p-8 md:p-16">
            <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">Stop paying the "App Tax."</h2>
                <p className="text-slate-400 max-w-2xl mx-auto">
                    Why pay for 5 different subscriptions when you only need one? Spatchy consolidates your entire stack.
                </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center">
                {/* LEFT: THE OLD WAY */}
                <div className="bg-slate-900/50 p-8 rounded-2xl border border-red-500/20 relative overflow-hidden">
                    <div className="absolute top-0 right-0 bg-red-500/10 text-red-400 text-xs font-bold px-3 py-1 rounded-bl-lg">THE OLD WAY</div>
                    <div className="space-y-4 opacity-70">
                        <div className="flex justify-between items-center p-3 bg-slate-800 rounded border border-white/5">
                            <span className="flex items-center gap-2 text-slate-400"><Layers size={16}/> CRM (e.g. Pipedrive)</span>
                            <span className="text-slate-500 font-mono">$50/mo</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-slate-800 rounded border border-white/5">
                            <span className="flex items-center gap-2 text-slate-400"><FileText size={16}/> Invoicing (e.g. QuickBooks)</span>
                            <span className="text-slate-500 font-mono">$30/mo</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-slate-800 rounded border border-white/5">
                            <span className="flex items-center gap-2 text-slate-400"><Calendar size={16}/> Scheduling (e.g. Jobber)</span>
                            <span className="text-slate-500 font-mono">$40/mo</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-slate-800 rounded border border-white/5">
                            <span className="flex items-center gap-2 text-slate-400"><Clock size={16}/> HR & Timesheets (e.g. Gusto)</span>
                            <span className="text-slate-500 font-mono">$35/mo</span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-slate-800 rounded border border-white/5">
                            <span className="flex items-center gap-2 text-slate-400"><Mail size={16}/> Email Marketing (e.g. Mailchimp)</span>
                            <span className="text-slate-500 font-mono">$20/mo</span>
                        </div>
                    </div>
                    <div className="mt-6 pt-6 border-t border-white/10 flex justify-between items-center">
                        <span className="text-red-400 font-bold">Total Chaos</span>
                        <span className="text-2xl font-bold text-white">$175/mo</span>
                    </div>
                </div>

                {/* RIGHT: THE SPATCHY WAY */}
                <div className="bg-gradient-to-br from-blue-900/40 to-slate-900 p-8 rounded-2xl border border-blue-500/30 relative overflow-hidden shadow-2xl shadow-blue-900/20 transform md:scale-105 transition-transform">
                    <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">THE SMART WAY</div>
                    <div className="flex flex-col items-center justify-center h-full py-8 text-center">
                        <div className="w-16 h-16 bg-gradient-to-tr from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                             <span className="text-white font-bold text-2xl">S</span>
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">Spatchy All-In-One</h3>
                        <p className="text-slate-400 mb-6 text-sm">Everything included. One login.</p>
                        <div className="text-5xl font-bold text-white mb-2">{startingPrice}<span className="text-lg text-slate-400 font-normal">/mo</span></div>
                        <p className="text-green-400 text-sm font-medium">You save $1,500+ per year</p>
                    </div>
                </div>
            </div>
        </div>
      </section>

      {/* --- SECTION 2: THE PILLARS --- */}
      <div className="space-y-32">
        {features.map((f, index) => (
          <section key={f.id} className="px-6 scroll-mt-24" id={f.id}>
            <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
                <div className={`order-2 ${index % 2 === 0 ? 'md:order-1' : 'md:order-2'}`}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className={`w-12 h-12 ${f.bg} rounded-xl flex items-center justify-center ${f.color}`}>
                          {f.icon}
                      </div>
                      {f.badge && (
                        <span className="px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold uppercase">
                          {f.badge}
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-bold tracking-wider text-slate-500 uppercase mb-2">{f.title}</div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">{f.headline}</h2>
                    <p className="text-slate-400 text-lg mb-6 leading-relaxed">
                        {f.description}
                    </p>
                    <ul className="space-y-4 mb-8">
                        {f.points.map((point, i) => (
                            <li key={i} className="flex items-start gap-3 text-slate-300 font-medium">
                                <CheckCircle2 className={`${f.color} shrink-0 mt-0.5`} size={20}/> 
                                {point}
                            </li>
                        ))}
                    </ul>
                    <Link 
                        to={f.primaryLink} 
                        className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-bold transition-colors"
                    >
                        Explore {f.title.split(' &')[0].split(' —')[0]} in depth <ArrowRight size={18} />
                    </Link>
                </div>
                <div className={`order-1 ${index % 2 === 0 ? 'md:order-2' : 'md:order-1'}`}>
                    <div className="bg-slate-800 rounded-2xl border border-white/10 p-2 shadow-2xl relative overflow-hidden group hover:scale-[1.02] transition-transform duration-500">
                        <img src={f.image} alt={`${f.title} Screenshot`} className="w-full h-auto rounded-lg shadow-lg"/>
                        <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent pointer-events-none"></div>
                    </div>
                </div>
            </div>
          </section>
        ))}

        {/* --- MOBILE APP PILLAR (custom — App Store badges instead of "Explore in depth" link) --- */}
        <section className="px-6 scroll-mt-24" id="mobile-app">
          <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center text-indigo-400">
                  <Smartphone size={24} />
                </div>
                <span className="px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-bold uppercase">
                  Now Available
                </span>
              </div>
              <div className="text-sm font-bold tracking-wider text-slate-500 uppercase mb-2">Mobile App</div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">Your crew. Your jobs. In their pocket.</h2>
              <p className="text-slate-400 text-lg mb-6 leading-relaxed">
                Built for the truck, not just the desk. Your team views their day, updates jobs, and flag follow-ups right from their phone. Real-time sync with the office. Available on iOS and Android, included on every plan.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "View today's schedule and assigned jobs",
                  "Update job status from the field — Scheduled, In Progress, Complete",
                  "Optimize your daily route inside the app",
                  "Mobile timesheets and time tracking",
                  "Real-time sync with your office and dispatch",
                  "iOS and Android — every plan, every user, no extra fees"
                ].map((point, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300 font-medium">
                    <CheckCircle2 className="text-indigo-400 shrink-0 mt-0.5" size={20}/> 
                    {point}
                  </li>
                ))}
              </ul>
              {/* App Store badges in place of "Explore in depth" link */}
              <div className="flex flex-col sm:flex-row items-start gap-3">
                <a 
                  href={IOS_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download Spatchy on the App Store"
                  className="inline-block transform hover:-translate-y-0.5 transition-transform"
                >
                  <img src={appStoreBadge} alt="Download on the App Store" className="h-12 w-auto" />
                </a>
                <a 
                  href={ANDROID_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get Spatchy on Google Play"
                  className="inline-block transform hover:-translate-y-0.5 transition-transform"
                >
                  <img src={googlePlayBadge} alt="Get it on Google Play" className="h-12 w-auto" />
                </a>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="bg-slate-800 rounded-2xl border border-white/10 p-2 shadow-2xl relative overflow-hidden group hover:scale-[1.02] transition-transform duration-500">
                <img src={mobileAppPreview} alt="Spatchy mobile app on iOS and Android" className="w-full h-auto rounded-lg shadow-lg"/>
                <div className="absolute inset-0 bg-gradient-to-tr from-white/5 to-transparent pointer-events-none"></div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* --- SECTION 3: THE NO GATEKEEPING PLEDGE --- */}
      <section className="mt-40 px-6">
        <div className="max-w-5xl mx-auto bg-slate-900 border border-slate-800 rounded-3xl p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500"></div>
            <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold uppercase mb-4">
                    Our Philosophy
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">We don't gatekeep features.</h2>
                <p className="text-slate-400 max-w-2xl mx-auto text-lg">
                    Most software companies punish you for growing. They hide the best features behind expensive "Enterprise" tiers. 
                    <br/><br/>
                    <span className="text-white font-bold">Not us.</span> At Spatchy, the solo operator gets the exact same power as the 50-person crew.
                </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 text-center">
                <div className="p-6 bg-slate-800/50 rounded-xl border border-white/5">
                    <Lock className="w-8 h-8 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-slate-500 font-bold mb-2">The Other Guys</h3>
                    <p className="text-sm text-slate-600">"Upgrade to Pro for Reporting"</p>
                </div>
                <div className="p-6 bg-slate-800/50 rounded-xl border border-white/5">
                    <Lock className="w-8 h-8 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-slate-500 font-bold mb-2">The Other Guys</h3>
                    <p className="text-sm text-slate-600">"Upgrade to Elite for Route Optimization"</p>
                </div>
                <div className="p-6 bg-blue-600 rounded-xl shadow-lg transform scale-105 border border-blue-400">
                    <Unlock className="w-8 h-8 text-white mx-auto mb-4" />
                    <h3 className="text-white font-bold mb-2">Spatchy</h3>
                    <p className="text-sm text-blue-100 font-medium">Everything Unlocked. Always.</p>
                </div>
            </div>
        </div>
      </section>

      {/* --- SECTION 4: THE FEATURE DIRECTORY --- */}
      <section className="mt-40 px-6">
        <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
                <h2 className="text-3xl font-bold mb-4">The Complete Directory</h2>
                <p className="text-slate-400">Explore everything Spatchy has to offer, down to the last detail.</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {featureDirectory.map((section, index) => (
                    <div key={index} className={`bg-slate-800/30 rounded-2xl p-6 border ${section.category === 'Smart Routing & Territory Planning' ? 'border-cyan-500/30 bg-cyan-900/10' : 'border-white/5'}`}>
                        <h4 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                            {section.category === 'Smart Routing & Territory Planning' 
                              ? <Route className="text-cyan-400" size={20}/> 
                              : section.category === 'Reporting & Analytics' 
                                ? <BarChart3 className="text-blue-500" size={20}/>
                                : <CheckCircle2 className="text-blue-500" size={20}/>}
                            {section.category}
                            {section.category === 'Smart Routing & Territory Planning' && (
                              <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">New</span>
                            )}
                        </h4>
                        <div className="space-y-3">
                            {section.items.map((item, i) => (
                                <Link 
                                    key={i} 
                                    to={`/features/${item.slug}`}
                                    className="group flex items-center justify-between p-3 rounded-lg bg-slate-900/50 hover:bg-slate-800 border border-transparent hover:border-blue-500/30 transition-all"
                                >
                                    <span className="text-slate-300 group-hover:text-white text-sm font-medium">{item.name}</span>
                                    <ArrowUpRight size={16} className="text-slate-600 group-hover:text-blue-400 transition-colors opacity-0 group-hover:opacity-100" />
                                </Link>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
      </section>

      {/* --- COMPARE CALLOUT --- */}
      <section className="mt-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-slate-400 mb-4 text-lg">Wondering how we stack up against Jobber?</p>
          <Link
            to="/compare/spatchy-vs-jobber"
            className="inline-flex items-center gap-2 px-8 py-4 bg-slate-800 hover:bg-slate-700 border border-white/10 hover:border-blue-500/40 text-white font-bold rounded-full transition-all"
          >
            See the Full Comparison <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* --- BOTTOM CTA --- */}
      <section className="mt-32 text-center px-6">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-blue-900/40 to-slate-900 border border-blue-500/20 rounded-3xl p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px] -z-10"></div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to get organized?</h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
                Join the growing number of service business owners who trust Spatchy to run their day-to-day.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a 
                    href={APP_SIGNUP_URL} 
                    className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-full transition-all shadow-lg shadow-blue-900/50 flex items-center gap-2"
                >
                    {/* wave1-fix 3f8a1c05: real trial is 30 days, not 14 */}
                    Start Your 30-Day Free Trial <ArrowRight size={18} />
                </a>
            </div>
            <p className="mt-6 text-sm text-slate-500">No credit card required. CAD & USD pricing available.</p>
        </div>
      </section>

    </div>
  );
};

export default FeaturesPage;